// analytics-events.ts — PSTUDIO-4784

/**
 * The Custom Events Library events the Analytics Events dropdown offers, with the
 * properties the library suggests for each. They are CUSTOM events, the ones CS
 * writes into the Analytics Events column of the CSV, and NOT the platform's
 * standard events, which are always sent and never appear in this list: the 7 rows of the "Custom Events Library" sheet
 * (ticket PSTUDIO-4784), names exactly as its "Event Name" column spells them
 * (Leo, product, 2026-11-02: "go with option 1"). The properties are the sheet's
 * "Suggested Properties (non-exhaustive)" column, in its order.
 */
const CUSTOM_EVENTS_LIBRARY_DEFINITIONS: ReadonlyArray<{
  name: string;
  properties: readonly string[];
}> = [
  {
    name: 'User Authenticated',
    properties: [
      'email_address',
      'first_name',
      'last_name',
      'account_id',
      'member_id',
    ],
  },
  {
    name: 'Survey Response Selected',
    properties: [
      'survey_prompt',
      'survey_format',
      'survey_response',
      'survey_default_value',
    ],
  },
  {
    name: 'Survey Feedback Provided',
    properties: [
      'survey_prompt',
      'survey_format',
      'survey_response',
      'survey_default_value',
      'free_text_prompt',
      'free_text_response',
    ],
  },
  { name: 'Workflow Started', properties: ['workflow_name'] },
  {
    name: 'API Call Executed',
    properties: ['api_endpoint', 'api_endpoint_description'],
  },
  {
    name: 'API Call Failed',
    properties: ['api_endpoint', 'api_status_code', 'api_response_text'],
  },
  {
    name: 'Disclaimer Answered',
    properties: ['disclaimer_text', 'disclaimer_response'],
  },
];

export const CUSTOM_EVENTS_LIBRARY: readonly string[] =
  CUSTOM_EVENTS_LIBRARY_DEFINITIONS.map((event) => event.name);

// A Map, not an object: a typed name such as `__proto__` or `constructor` must
// not find an entry that was never defined.
const LIBRARY_EVENT_PROPERTIES: ReadonlyMap<string, readonly string[]> =
  new Map(
    CUSTOM_EVENTS_LIBRARY_DEFINITIONS.map((event) => [
      event.name,
      event.properties,
    ])
  );

/**
 * What the select hands back for an option is NOT always the name: the design
 * system rewrites every space in an option's `value` to an underscore
 * (`select-option.wc.ts`, `@watch('value')`), and these names have spaces.
 * `toSelectOptionValue` is the value a library name is declared with (and the
 * value the select is given when the cell holds one), and `fromSelectOptionValue`
 * turns what the select emitted back into the library name. A name that is not
 * a library one (typed by the user) is returned unchanged by both: its spaces
 * are the user's own. Limit: a typed name that equals the underscore form of a
 * library name (`User_Authenticated`) is indistinguishable from a pick of that
 * option, because the select reports both the same way, and becomes the library
 * event.
 */
export function toSelectOptionValue(name: string): string {
  return CUSTOM_EVENTS_LIBRARY.includes(name) ? name.replace(/ /g, '_') : name;
}

export function fromSelectOptionValue(emitted: string): string {
  return (
    CUSTOM_EVENTS_LIBRARY.find(
      (name) => toSelectOptionValue(name) === emitted
    ) ?? emitted
  );
}

/**
 * The cell text a chosen event name becomes: ONE event object. The compiler
 * (`serializeEvent` in agent-compiler 1.11.0) reads an Analytics Events cell as
 * an object or an array of objects and drops anything else, so a bare name would
 * be saved and then never collected. Returns `null` for a name that is empty
 * after trimming — there is no event to write.
 *
 * A library event also carries its suggested properties, each with an empty
 * value, under `additional_properties`: that is the key the compiler reads an
 * event's properties from (`getAnalyticsEvent` emits it as `properties`), and
 * `AnalyticsEventSchema` is `.strict()`, so a property written as a top-level key
 * would fail that check: the compiler's `validateEvent` reports it as an error
 * on the node, and `getAnalyticsEvent` leaves such an event out of the output.
 * A name that is not a library one gets no properties.
 */
export function buildAnalyticsEventText(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const properties = LIBRARY_EVENT_PROPERTIES.get(trimmed);
  if (!properties) return JSON.stringify({ event_name: trimmed });
  return JSON.stringify({
    event_name: trimmed,
    additional_properties: Object.fromEntries(
      properties.map((property) => [property, ''])
    ),
  });
}

/**
 * The event name a cell holds when it holds ONE event object with a text
 * `event_name`, whatever else is in it — `additional_properties` filled in,
 * `send`, `variables`, `global_props` and the rest — else `''`.
 *
 * The cell is a name picker: a user who picks or types an event and then adds
 * fields in the JSON editor still has that event, so the cell keeps showing its
 * name instead of the whole object. For the same reason re-picking the event the
 * cell already shows writes nothing (`commitAnalyticsEventChoice`), which keeps
 * the fields the user added. A cell with no single name to show — an array of
 * events, an object without a text `event_name`, text that is not JSON — gives
 * `''`, and the picker then shows the cell's own text instead of guessing.
 */
export function readAnalyticsEventName(value: unknown): string {
  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return '';
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return '';
  }
  const name = (parsed as { event_name?: unknown }).event_name;
  return typeof name === 'string' ? name : '';
}

/**
 * What a select showing this cell displays, always on ONE line: the event name
 * when the cell holds a single event object, otherwise the cell's own text with
 * its layout removed (parsed JSON is re-serialised compactly; anything else has
 * its whitespace runs collapsed).
 *
 * One line is a requirement, not a nicety: a text field strips newlines from the
 * value it is given, so a multi-line JSON label would read back as "different
 * from the committed text" and a blur would commit the flattened text as a
 * custom event, overwriting the cell.
 */
export function analyticsEventCellLabel(value: unknown): string {
  const name = readAnalyticsEventName(value);
  if (name) return name;

  const text =
    typeof value === 'string'
      ? value
      : value == null
        ? ''
        : JSON.stringify(value);
  try {
    return JSON.stringify(JSON.parse(text));
  } catch {
    return text.replace(/\s+/g, ' ').trim();
  }
}
